import {
  WSAlert,
  WSCard,
  WSElement,
  WSGrid,
  WSList,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import classNames from "classnames";
import { useMemo } from "react";
import { useQueryInternalAccountBalance } from "../../../../query/banking/queries/useQueryInternalAccountBalance";
import { useQueryInternalAccounts } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorActiveTaxWithholdingAccount } from "../../../../shared/selectors/selectorActiveTaxWithholdingAccount";
import { TransferBalance } from "../../types";
import { TransferSchema } from "./TransferSchema";
import styles from "./index.module.scss";

type Props = {
  balance: TransferBalance;
  onDeposit: () => void;
  onWithdrawal: () => void;
};

export const FormType: React.FC<Props> = ({
  balance,
  onDeposit,
  onWithdrawal
}) => {
  const balanceName = useMemo(
    () => (balance === "wallet" ? "Wingspan Wallet" : "Tax Withholdings"),
    [balance]
  );

  const queryInternalAccounts = useQueryInternalAccounts();

  const activeBankingAccount = useMemo(() => {
    return queryInternalAccounts.data?.find(selectorActiveBankingAccount);
  }, [queryInternalAccounts.data]);

  const activeTaxWithholdingAccount = useMemo(() => {
    return queryInternalAccounts.data?.find(
      selectorActiveTaxWithholdingAccount
    );
  }, [queryInternalAccounts.data]);

  const queryBankingBalance = useQueryInternalAccountBalance(
    activeBankingAccount?.internalAccountId!,
    { enabled: !!activeBankingAccount }
  );

  const queryTaxWithholdingBalance = useQueryInternalAccountBalance(
    activeTaxWithholdingAccount?.internalAccountId!,
    { enabled: !!activeTaxWithholdingAccount }
  );

  const bankingBalance = queryBankingBalance.data?.balance;
  const taxWithholdingsBalance = queryTaxWithholdingBalance.data?.balance;

  const currency =
    balance === "wallet"
      ? activeBankingAccount?.currency
      : activeTaxWithholdingAccount?.currency;

  const balanceTotal =
    balance === "wallet" ? bankingBalance : taxWithholdingsBalance;

  const isDepositDisabled =
    balanceTotal !== undefined ? balanceTotal <= 0 : false;

  return (
    <WSList gap="2XL">
      <WSElement>
        <WSSectionToolbar title="Where would you like to send your funds?" />
      </WSElement>

      <WSGrid gutter="M">
        <WSGrid.Item span={{ s: "6" }}>
          <WSCard
            header="Transfer to bank"
            onClick={isDepositDisabled ? undefined : onWithdrawal}
            className={classNames({ [styles.disabled]: isDepositDisabled })}
          >
            <WSText.ParagraphSm color="gray500">
              Send funds from your {balanceName} to your personal bank account.
              <br />
              (1 business day)
            </WSText.ParagraphSm>
            <TransferSchema type="withdrawal" mt="XL" />
          </WSCard>

          {isDepositDisabled && (
            <WSAlert theme="info" mt="XL">
              There is no balance (
              {formatMoney(balanceTotal!, currency || "USD")}) to transfer from
              your {balanceName}
            </WSAlert>
          )}
        </WSGrid.Item>
        <WSGrid.Item span={{ s: "6" }}>
          <WSCard header={`Transfer to ${balanceName}`} onClick={onDeposit}>
            <WSText.ParagraphSm color="gray500">
              Send funds from your bank account to your {balanceName}.<br />
              (3-5 business days)
            </WSText.ParagraphSm>

            <TransferSchema type="deposit" mt="XL" />
          </WSCard>
        </WSGrid.Item>
      </WSGrid>
    </WSList>
  );
};
